import type { Plan } from 'services/repositories';

/**
 * Converts the plan's `paymentInterval` + `intervalCount` (surfaced by the
 * BFF on `/api/v2/product/{code}`, FIN-7350) into a plain months count for
 * {@link resolveRecurringInterval}.
 *
 * - `'Year'` -> `12 * (intervalCount || 1)` months.
 * - `'Month'` -> `intervalCount || 1` months.
 * - `paymentInterval` absent, or anything other than `'Year'`/`'Month'`
 *   (the field is typed as a closed union but comes from a backend response,
 *   so a runtime value outside that union isn't guaranteed) -> `undefined`,
 *   which keeps `resolveRecurringInterval`'s annual default instead of
 *   silently treating an unrecognized unit as months.
 * - A non-positive `intervalCount` (`0` or negative) is also treated as
 *   invalid -> `undefined`, rather than passing a negative/zero count
 *   through to Apple.
 */
export function getRecurringIntervalInMonths(
  plan: Pick<Plan, 'paymentInterval' | 'intervalCount'> | undefined
): number | undefined {
  if (plan?.paymentInterval !== 'Year' && plan?.paymentInterval !== 'Month') {
    return undefined;
  }

  if (plan.intervalCount !== undefined && plan.intervalCount <= 0) {
    return undefined;
  }

  const count = plan.intervalCount || 1;

  return plan.paymentInterval === 'Year' ? 12 * count : count;
}
